// @ts-check

import { api } from '@/api'

import {
  copyExpense,
  createExpense,
  deleteExpense,
  getExpenseActivityLog,
  getExpenseById,
  getExpenseLedgerAccounts,
  getExpenseQuestionTree,
  getExpenses,
  getLinkableExpenses,
  linkBankTransactionsToExpense,
  updateExpense
} from '@/api/expenses'

import {
  copyIncome,
  createIncome,
  deleteIncome,
  getIncomeActivityLog,
  getIncomeById,
  getIncomeLedgerAccounts,
  getIncomeQuestionTree,
  getIncomes,
  getLinkableIncomes,
  linkBankTransactionsToIncome,
  updateIncome
} from '@/api/incomes'

import { getAccountTransactions } from '@/api/accountTransactions'
import { UnsupportedEntryType } from '@/entries/errors/unsupported-entry-type'
import { useMemoize } from '@vueuse/core'

export const getEntriesOfType = async (type, params, page) => {
  if (type === 'account-transactions') {
    return getAccountTransactions(params, page)
  }
  if (type === 'expense' || type === 'expenses') {
    return getExpenses(params, page)
  }
  if (type === 'income' || type === 'incomes') {
    return getIncomes(params, page)
  }

  const requestBody = { params: { ...params } }

  if (page) {
    requestBody.params = Object.assign(requestBody.params, {
      page
    })
  }

  return api.get(`dashboard/actions`, requestBody)
}

/**
 * @param {EntryType} type
 * @param {{ limit?: number }} payload
 * @returns {Promise<EntryResource[]>}
 */
export const getLinkableEntriesOfType = async (type, payload = {}) => {
  if (type === 'expense') {
    return getLinkableExpenses(payload)
  }

  if (type === 'income') {
    return getLinkableIncomes(payload)
  }

  throw new UnsupportedEntryType(type)
}

/**
 * Creates a new entry resource based on the given type.
 * @param {EntryType} type
 * @param {*} payload
 * @returns {Promise<EntryResource>}
 */
export const createEntry = async (type, payload) => {
  if (type === 'expense') {
    return createExpense(payload)
  }

  if (type === 'income') {
    return createIncome(payload)
  }

  throw new UnsupportedEntryType(type)
}

/**
 * Updates an existing entry resource based on the given type.
 * @param {EntryType} type
 * @param {EntryResource['id']} id
 * @param {*} payload
 * @returns {Promise<EntryResource>}
 */
export const updateEntry = async (type, id, payload) => {
  if (type === 'expense') {
    return updateExpense(id, payload)
  }

  if (type === 'income') {
    return updateIncome(id, payload)
  }

  throw new UnsupportedEntryType(type)
}

/**
 * Gets entry resource based on the given resource id and type of the entry.
 * @param {EntryResource['id']} id
 * @param {EntryType} type
 */
export const getEntryById = (id, type) => {
  if (type === 'expense') {
    return getExpenseById(id)
  }

  if (type === 'income') {
    return getIncomeById(id)
  }

  throw new UnsupportedEntryType({ type })
}

export const linkBankTransactionsToEntry = async (
  type,
  bankTransactions,
  entryId,
  amount
) => {
  const transactionsForApi = [...bankTransactions]

  if (amount && transactionsForApi.length === 1) {
    transactionsForApi[0].amount = amount
  }

  if (type === 'expense') {
    return linkBankTransactionsToExpense(transactionsForApi, entryId)
  }
  return linkBankTransactionsToIncome(transactionsForApi, entryId)
}

export function getInvoicePreview(incomeId) {
  return api.get(`incomes/${incomeId}/preview`)
}

export function getOriginalInvoiceOfReminder(incomeId) {
  return api.get(`incomes/${incomeId}/original-invoice-of-reminder`)
}

export const fetchAttachmentImage = async (id) => {
  const { data: attachment } = await api.get(`attachments/${id}`)
  return attachment
}

export function getNextAvailableVoucherDate() {
  return api.get('company/next-available-voucher-date')
}

/**
 * @param {Pick<EntryResource, 'id'|'type'>} entry
 * @returns {Promise<EntryLogResource[]>}
 */
export const getEntryLog = (entry) => {
  if (entry.type === 'expense') {
    return getExpenseActivityLog(entry.id)
  }

  if (entry.type === 'income') {
    return getIncomeActivityLog(entry.id)
  }

  throw new UnsupportedEntryType(entry)
}

/**
 * @param {Pick<EntryResource, 'id'|'type'>} entry
 * @param {LedgerAccountQueryParams} params
 */
export function getLedgerAccounts(entry, params = {}) {
  if (entry.type === 'expense') {
    return getExpenseLedgerAccounts(entry.id, params)
  }

  if (entry.type === 'income') {
    return getIncomeLedgerAccounts(entry.id, params)
  }

  throw new UnsupportedEntryType(entry)
}

/**
 * Returns ocr data for the attachment with the given id.
 *
 * @param {AttachmentResource['id']} attachmentId Name to search the suppliers with.
 * @returns {Promise<?AttachmentOcrResource>}
 */
export const getOcrDataForAttachment = async (attachmentId) => {
  try {
    const { data: ocrData } = await api.get(`attachments/${attachmentId}/ocr`)
    return ocrData
  } catch {
    return null
  }
}

/**
 * Update only the description of an expense
 *
 * @param {String|Number} expenseId Expense id
 * @param {String} description Expense description
 * @returns {Promise<any>}
 */
export const updateExpenseDescription = async (expenseId, description) =>
  api.put(`expenses/${expenseId}/update-description`, {
    description
  })

/**
 * @param {Pick<EntryResource, 'id'|'type'>} entry
 * @returns {Promise<EntryResource>}
 */
export const copyEntry = async (entry) => {
  if (entry.type === 'expense') {
    return copyExpense(entry.id)
  }

  if (entry.type === 'income') {
    return copyIncome(entry.id)
  }

  throw new UnsupportedEntryType(entry)
}

/**
 * @param {Pick<EntryResource, 'id'|'type'>} entry
 */
export const deleteEntry = async (entry) => {
  if (entry.type === 'expense') {
    return deleteExpense(entry.id)
  }

  if (entry.type === 'income') {
    return deleteIncome(entry.id)
  }

  throw new UnsupportedEntryType(entry)
}

/**
 * @type {import('@vueuse/core').UseMemoizeReturn<Promise<EntryQuestionTree>, [EntryType, EntryQuestionTreeParams]>}}
 */
export const getEntryQuestionTree = useMemoize(
  async (type = 'expense', params = {}) => {
    if (type === 'expense') {
      return getExpenseQuestionTree(params)
    }

    if (type === 'income') {
      return getIncomeQuestionTree(params)
    }

    throw new UnsupportedEntryType({ type })
  }
)

/**
 * @param {LocalizedPayload} params
 * @returns {Promise<Record<string, string>>}
 */
export const getEntryDescriptionRequirements = async (params) => {
  const res = await api.get('entries/description-requirements', {
    params
  })

  return res.data
}
